import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyFormFieldAppearance as MatFormFieldAppearance } from '@angular/material/legacy-form-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputObservable } from '@y42/angular/util';
import { IListViewComponent, ListViewColDef } from '@y42/core/list-view';
import {
  getDatasourceListFrameworkComponents,
  getDatasourceListViewGridOptions,
  SubEntryRendererParams,
} from '@y42/feature/shared/list-view';
import { UserQuery } from '@y42/user/state';
import {
  CellClickedEvent,
  GridApi,
  GridOptions,
  IRowNode,
  RowClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { combineLatest, debounceTime, map, Observable, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { DatasourceListViewData } from '../../state';
import { DatasourceListViewColumnDefsService } from '../list-view';

@UntilDestroy()
@Component({
  selector: 'y42-datasource-table-select-list-view',
  templateUrl: './datasource-table-select-list-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatasourceTableSelectListViewComponent implements IListViewComponent, OnChanges {
  constructor(
    private readonly userQuery: UserQuery,
    private readonly datasourceListViewColumnDefsService: DatasourceListViewColumnDefsService,
  ) {
    this.setCheckboxSelectionsOnSelectedIdsChange().subscribe();
  }

  @Input() searchFieldAppearance: MatFormFieldAppearance = 'standard';
  @Input() hideListViewTitle = false;
  @Input() multiSelect = false;
  @Input() previewButton?: boolean = undefined;
  @Input() selectionDisabled = false;
  @Input() tablePreview?: SubEntryRendererParams['tablePreview'];
  @Input() hasPadding = true;
  @Input() activeTablePath?: string;

  @Input()
  @InputObservable()
  selectedIds?: Array<DatasourceListViewData['child_$path']>;
  selectedIds$!: Observable<Array<DatasourceListViewData['child_$path']> | undefined>;

  @Output() selectionChanged = new EventEmitter<DatasourceListViewData[]>();

  gridApi?: GridApi;
  private readonly gridReady$ = new Subject<void>();

  gridOptions: GridOptions = this.getGridOptions();

  columnDefs: ListViewColDef[] = this.getColOptions();

  readonly frameworkComponents: GridOptions['components'] = (() => {
    const components = getDatasourceListFrameworkComponents() || {};
    delete components.jobStatus;
    return components;
  })();

  ngOnChanges(changes: SimpleChanges) {
    if (changes.multiSelect || changes.selectionDisabled) {
      this.gridOptions = this.getGridOptions();
    }
    if (changes.previewButton || changes.selectionDisabled || changes.tablePreview) {
      this.columnDefs = this.getColOptions();
    }
  }

  cellClicked(evt: CellClickedEvent) {
    const node = evt.node!;
    if (node.group && (node.aggData as DatasourceListViewData).last_import_date) {
      node.setExpanded(!node.expanded);
    }
  }

  selectDatasource(evt: SelectionChangedEvent) {
    const api = evt.api;
    const data: DatasourceListViewData[] = api.getSelectedNodes().map((node) => node.data);

    if (data) {
      this.selectionChanged.emit(data);
    }
  }

  rowClicked(evt: RowClickedEvent) {
    const data: DatasourceListViewData[] = [evt.node.data];

    if (data) {
      this.selectionChanged.emit(data);
    }
  }

  gridReady({ api }: { api?: GridApi | null }) {
    this.gridApi = api!;
    this.gridReady$.next();
  }

  private getColOptions() {
    return Object.values(
      (() => {
        const colDef = this.datasourceListViewColumnDefsService.columnDefs({
          locale: this.userQuery.getActiveUser()?.locale,
          disableStarring: true,
          disableTagging: true,
          disableSelection: this.selectionDisabled,
          previewButton: this.previewButton,
          tablePreview: this.tablePreview,
          hideDetails: true,
        });
        return colDef;
      })(),
    );
  }

  private getGridOptions() {
    return getDatasourceListViewGridOptions({
      importActiveRowClassRule: (__) => false, // todo
      hideSubEntries: false,
      selectionDisabled: this.selectionDisabled,
      rowSelection: this.multiSelect ? 'multiple' : 'single',
      hideDetails: true,
    });
  }

  private setCheckboxSelectionsOnSelectedIdsChange() {
    return combineLatest([this.gridReady$, this.selectedIds$.pipe(startWith([]))]).pipe(
      debounceTime(200),
      map(([__, selectedIds]) => selectedIds as Array<DatasourceListViewData['child_$path']>),
      switchMap((selectedIds) => {
        if (this.selectionDisabled) {
          return of(null);
        }

        let firstNodeToPosition: IRowNode | null = null;
        this.gridApi?.forEachNode((node) => {
          const data: DatasourceListViewData | undefined = node?.data;
          const childId = data?.child_$path;
          if (!node.group && childId && selectedIds?.includes(childId)) {
            if (!node.isSelected()) {
              node.setSelected(true, undefined, true);
              if (node.parent && node.parent.field && node.parent.field === 'id') {
                node.parent.setExpanded(true);
                if (!firstNodeToPosition) {
                  firstNodeToPosition = node;
                }
              }
            }
          }
        });

        return firstNodeToPosition
          ? of(firstNodeToPosition).pipe(
              debounceTime(500),
              tap((node) => this.gridApi?.ensureNodeVisible(node, 'middle')),
            )
          : of(null);
      }),
      untilDestroyed(this),
    );
  }
}
